/**
 * AdminPageState — the ONE standard for admin page chrome: a consistent
 * page header plus the loading / empty / error states every admin screen
 * shares. Replaces the ad-hoc spinners, blank screens, and bespoke error
 * boxes scattered across the admin tree. Theme-driven — no hex.
 *
 *   <AdminPageHeader/>   eyebrow + display title + subtitle + actions
 *   <AdminLoading/>      centered spinner
 *   <AdminEmpty/>        /brand Aspect 10 empty state (+ optional CTA)
 *   <AdminError/>        error banner with a Retry button
 *   <AdminPageState/>    convenience switch over loading/error/empty
 */
import type { ReactNode } from "react";
import { ActivityIndicator, Text, View } from "react-native";

import { RADII, SPACING, STROKES } from "../../../lib/brand";
import type { PaneTheme } from "../../brand/paneThemes";
import { AdminButton } from "./AdminButton";

export type AdminPageHeaderProps = {
  theme: PaneTheme;
  eyebrow: string;
  title: string;
  subtitle?: string;
  /** Right-aligned actions (buttons, links). */
  actions?: ReactNode;
};

export function AdminPageHeader({
  theme,
  eyebrow,
  title,
  subtitle,
  actions,
}: AdminPageHeaderProps) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "flex-end",
        justifyContent: "space-between",
        gap: SPACING.md,
        flexWrap: "wrap",
        marginBottom: SPACING.lg,
      }}
    >
      <View style={{ flexShrink: 1, minWidth: 0 }}>
        <Text
          style={{
            fontFamily: theme.bodyBoldFont,
            fontSize: 10,
            letterSpacing: 3,
            textTransform: "uppercase",
            color: theme.accent,
          }}
        >
          {eyebrow}
        </Text>
        <Text
          style={{
            marginTop: 6,
            fontFamily: theme.displayMediumFont,
            fontSize: 28,
            lineHeight: 32,
            color: theme.textOnSurface,
          }}
        >
          {title}
        </Text>
        {subtitle ? (
          <Text
            style={{
              marginTop: 4,
              fontFamily: theme.bodyFont,
              fontSize: 13,
              color: theme.textOnSurfaceDim,
            }}
          >
            {subtitle}
          </Text>
        ) : null}
      </View>
      {actions != null ? (
        <View style={{ flexDirection: "row", alignItems: "center", gap: 10 }}>
          {actions}
        </View>
      ) : null}
    </View>
  );
}

export function AdminLoading({
  theme,
  label,
}: {
  theme: PaneTheme;
  label?: string;
}) {
  return (
    <View style={{ paddingVertical: 48, alignItems: "center", gap: 12 }}>
      <ActivityIndicator size="small" color={theme.accent} />
      {label ? (
        <Text
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 12,
            color: theme.textOnSurfaceDim,
          }}
        >
          {label}
        </Text>
      ) : null}
    </View>
  );
}

export type AdminEmptyProps = {
  theme: PaneTheme;
  /** Optional uppercase eyebrow above the title. */
  eyebrow?: string;
  title: string;
  body?: string;
  /** Optional call-to-action button. */
  ctaLabel?: string;
  onCta?: () => void;
};

export function AdminEmpty({
  theme,
  eyebrow,
  title,
  body,
  ctaLabel,
  onCta,
}: AdminEmptyProps) {
  return (
    <View
      style={{
        backgroundColor: theme.surfaceBg,
        borderWidth: STROKES.hairline,
        borderColor: theme.border,
        borderRadius: RADII.md,
        padding: 28,
        alignItems: "center",
        justifyContent: "center",
        gap: 8,
      }}
    >
      {eyebrow ? (
        <Text
          style={{
            fontFamily: theme.bodySemiBoldFont,
            fontSize: 10,
            letterSpacing: 2.2,
            textTransform: "uppercase",
            color: theme.accent,
          }}
        >
          {eyebrow}
        </Text>
      ) : null}
      <Text
        style={{
          fontFamily: theme.displayMediumFont,
          fontSize: 22,
          color: theme.textOnSurface,
          textAlign: "center",
        }}
      >
        {title}
      </Text>
      {body ? (
        <Text
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 12.5,
            color: theme.textOnSurfaceDim,
            textAlign: "center",
            maxWidth: 340,
            marginBottom: ctaLabel ? 6 : 0,
          }}
        >
          {body}
        </Text>
      ) : null}
      {ctaLabel && onCta ? (
        <AdminButton theme={theme} label={ctaLabel} onPress={onCta} />
      ) : null}
    </View>
  );
}

export function AdminError({
  theme,
  message,
  onRetry,
}: {
  theme: PaneTheme;
  message: string;
  onRetry?: () => void;
}) {
  return (
    <View
      style={{
        flexDirection: "row",
        alignItems: "center",
        gap: 12,
        backgroundColor: theme.surfaceBg,
        borderWidth: STROKES.hairline,
        borderColor: theme.border,
        borderLeftWidth: STROKES.heavy,
        borderLeftColor: theme.destructive,
        borderRadius: RADII.md,
        paddingVertical: 12,
        paddingHorizontal: 14,
      }}
    >
      <View style={{ flex: 1, gap: 2 }}>
        <Text
          style={{
            fontFamily: theme.bodySemiBoldFont,
            fontSize: 12.5,
            color: theme.textOnSurface,
          }}
        >
          Something went wrong
        </Text>
        <Text
          style={{
            fontFamily: theme.bodyFont,
            fontSize: 12,
            color: theme.textOnSurfaceDim,
          }}
        >
          {message}
        </Text>
      </View>
      {onRetry ? (
        <AdminButton
          theme={theme}
          label="Retry"
          variant="outline"
          size="sm"
          onPress={onRetry}
        />
      ) : null}
    </View>
  );
}

export type AdminPageStateProps = {
  theme: PaneTheme;
  loading: boolean;
  error?: string | null;
  onRetry?: () => void;
  /** When true (and not loading/error), render the empty state. */
  isEmpty?: boolean;
  emptyTitle?: string;
  emptyBody?: string;
  loadingLabel?: string;
  children: ReactNode;
};

/**
 * Convenience switch: shows loading, then error (with retry), then empty,
 * else renders children. Keeps every screen's load/empty/error identical.
 */
export function AdminPageState({
  theme,
  loading,
  error,
  onRetry,
  isEmpty,
  emptyTitle = "Nothing here yet",
  emptyBody,
  loadingLabel,
  children,
}: AdminPageStateProps) {
  if (loading) return <AdminLoading theme={theme} label={loadingLabel} />;
  if (error)
    return <AdminError theme={theme} message={error} onRetry={onRetry} />;
  if (isEmpty)
    return <AdminEmpty theme={theme} title={emptyTitle} body={emptyBody} />;
  return <>{children}</>;
}
